import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";

export default function MeshCoreInfo() {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="meshcore-info-page">
      <div className="overline mb-3">/info — meshcore.co.uk</div>
      <h1 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-6">
        What is MeshCore?
      </h1>
      <p className="text-xl text-slate-300 leading-relaxed mb-10">
        MeshCore is an open-source firmware for license-free LoRa radios that turns commodity hardware into
        nodes on a resilient, peer-to-peer text messaging network. No internet, no cell towers, no servers.
      </p>

      <div className="prose-mesh">
        <h2>Why MeshCore</h2>
        <p>
          MeshCore was designed for one job: deliver short text messages reliably across long distances using cheap
          LoRa radios. It strips away the complexity of LoRaWAN gateways and the chattiness of legacy mesh protocols,
          and replaces them with a clean store-and-forward routing layer.
        </p>

        <h2>Three Node Roles</h2>
        <ul>
          <li><strong>Companion</strong> — your personal radio. Pairs via Bluetooth to a phone app for sending/reading messages.</li>
          <li><strong>Repeater</strong> — a fixed node that relays packets it hears to extend mesh range across hills, valleys, and dead zones.</li>
          <li><strong>Room Server / Observer</strong> — a node connected to a Raspberry Pi or PC that bridges traffic to MQTT for monitoring or web frontends.</li>
        </ul>

        <h2>How packets travel</h2>
        <p>
          When you send a message, your radio broadcasts a small encrypted LoRa packet. Any repeater in range will
          rebroadcast it once, with simple loop suppression. The mesh is fully distributed — no central servers, no
          required infrastructure. As more people join, the network's coverage and resilience automatically grow.
        </p>

        <h2>Security</h2>
        <p>
          Public channels are readable by anyone on the same channel preset. Private channels are end-to-end encrypted
          with a shared symmetric key. Direct messages are encrypted to the recipient's public key. Keys never leave
          your device.
        </p>

        <h2>Hardware</h2>
        <p>
          MeshCore runs on common ESP32 and nRF52 LoRa boards. Most members start with one of:
        </p>
        <ul>
          <li>Heltec WiFi LoRa 32 V3/V4 — solid, cheap (~$25), great for repeaters</li>
          <li>RAK4631 / RAK Wireless WisBlock — best battery life, ideal as companions or solar repeaters</li>
          <li>LilyGo T-Beam — built-in GPS, popular for portable use</li>
          <li>Xiao ESP32S3 + WIO LoRa expansion — tiny, low power</li>
        </ul>
        <p>
          See our <Link to="/devices">Devices guide</Link> for flashing instructions per board.
        </p>

        <h2>Regional Groups</h2>
        <ul>
          <li>
            Cumberland Valley PA (MDT){" "}
            <a href="https://www.facebook.com/share/g/1GUU5PWySM/" target="_blank" rel="noopener noreferrer">
              Facebook <ExternalLink className="w-3 h-3 inline" />
            </a>{" "}
            <a href="https://discord.gg/2PZfTX2wR" target="_blank" rel="noopener noreferrer">
              Discord <ExternalLink className="w-3 h-3 inline" />
            </a>
          </li>
          <li>
            NY Capital Region{" "}
            <a href="https://www.upst8me.sh/" target="_blank" rel="noopener noreferrer">
              Website <ExternalLink className="w-3 h-3 inline" />
            </a>{" "}
            <a href="https://discord.com/invite/FvajRmXEsb" target="_blank" rel="noopener noreferrer">
              Discord <ExternalLink className="w-3 h-3 inline" />
            </a>
          </li>
        </ul>

        <h2>External resources</h2>
        <ul>
          <li>
            <a href="https://meshcore.io" target="_blank" rel="noopener noreferrer">
              meshcore.io <ExternalLink className="w-3 h-3 inline" />
            </a>{" "}
            — official project site
          </li>
          <li>
            <a href="http://flasher.meshcore.io/" target="_blank" rel="noopener noreferrer">
              flasher.meshcore.io <ExternalLink className="w-3 h-3 inline" />
            </a>{" "}
            — browser-based firmware flasher
          </li>
          <li>
            <a href="https://github.com/meshcore-dev/MeshCore" target="_blank" rel="noopener noreferrer">
              github.com/meshcore-dev/MeshCore <ExternalLink className="w-3 h-3 inline" />
            </a>{" "}
            — official meshcore github
          </li>
        </ul>
      </div>
    </div>
  );
}
